import { DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import {
  ApplicationConfig,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import {
  ANTRAGS_OVERVIEW_UI_MAP,
  APP_ENVIRONMENT,
  APP_VERSION,
  AppWebConfig,
  AUTH_SESSION_STORAGE_TYPE,
  AuthenticationFacade,
  ConfigSettingsService,
  coreReducers,
  CustomErrorHandler,
  MessageBase,
  ModalServiceBase,
  NAV_LINK,
  NoKeyMissingTranslationHandler,
  PERSISTENT_STORAGE,
  readFromJson,
  StorageType,
  TelemetryService,
  TranslationInitializationService,
} from '@ebeihilfe/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ApplicationsDeleteFacade, ApplicationsDetailsFacade } from '@ebeihilfe/einreichung-new/facades';
import { KURZANTRAG_CONFIG } from '@ebeihilfe/einreichung-old';
import { layoutFeature, ModalService } from '@ebeihilfe/web-logic';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools, StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MissingTranslationHandler, TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { provideToastr } from 'ngx-toastr';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { VERSION } from '../../../../environments/version';
import jsonData from '../config/antrags-overview-ui-data.json';
import { getKurzantragConfig } from '../config/kurzantrag-config';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { CustomComponentModule } from './components';
// will be loaded via customer copy
import { CustomerAppModule } from './customer.app.module';
import { AppPagesModule } from './feature/pages/app-pages.module';
import * as NavLinks from './nav-links/index';
import { MessageService } from './services';
import { WebPreferencesImpl } from './services/preferences/web-preferences.service';
import { SentryService } from './services/sentry/sentry.service';
import { WebTelemetryService } from './services/telemetry/telemetry.service';

registerLocaleData(localeDe, 'de');

export const startAsyncInitInConfigSettings =
  (
    authenticationService: AuthenticationFacade,
    configService: ConfigSettingsService,
    translate: TranslationInitializationService,
    sentryService: SentryService,
  ) =>
  async (): Promise<void> => {
    await configService.loadConfigProviders();
    await authenticationService.initialize();
    await translate.initializeTranslate();
    await sentryService.init();
  };

if (environment.production) {
  enableProdMode();
}

export const appConfig: ApplicationConfig = {
  providers: [
    ApplicationsDeleteFacade,
    ApplicationsDetailsFacade,
    importProvidersFrom(
      !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
      AngularSvgIconModule.forRoot(),
      AppPagesModule,
      BrowserModule,
      CustomerAppModule,
      CustomComponentModule,
      HttpClientModule,
      MatButtonModule,
      MatDialogModule,
      TranslateModule.forRoot({
        missingTranslationHandler: { provide: MissingTranslationHandler, useClass: NoKeyMissingTranslationHandler },
      }),
    ),
    provideAnimations(),
    provideToastr(),
    { provide: PERSISTENT_STORAGE, useClass: WebPreferencesImpl },
    {
      provide: AUTH_SESSION_STORAGE_TYPE,
      useValue: StorageType.SESSION,
    },
    {
      provide: APP_VERSION,
      useValue: VERSION,
    },
    {
      provide: APP_ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: ANTRAGS_OVERVIEW_UI_MAP,
      useValue: readFromJson(jsonData),
    },
    provideAppInitializer(() => {
      const initializerFn = startAsyncInitInConfigSettings(
        inject(AuthenticationFacade),
        inject(ConfigSettingsService),
        inject(TranslationInitializationService),
        inject(SentryService),
      );
      return initializerFn();
    }),
    {
      provide: KURZANTRAG_CONFIG,
      deps: [TranslateService],
      useFactory: getKurzantragConfig,
    },
    {
      provide: ModalServiceBase,
      useClass: ModalService,
    },
    {
      provide: MessageBase,
      useClass: MessageService,
    },
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    {
      provide: TelemetryService,
      useClass: WebTelemetryService,
    },
    ...Object.values(NavLinks).map((navLink) => ({
      provide: NAV_LINK,
      useClass: navLink,
      multi: true,
    })),
    DatePipe,
    ...AppWebConfig.providers,
    provideStore(coreReducers),
    provideEffects([]),
    provideState(layoutFeature),
    provideRouterStore(),
    provideStoreDevtools(),
    provideRouter(routes, withComponentInputBinding()),
  ],
};
